import React, { useCallback, useMemo, useState } from 'react';
import { useWindowDimensions, View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import HtmlRenderer from 'react-native-render-html';

import { COMPLETE_PRACTICAL_TRAINING_ASSIGNMENT } from '../../graphql/training';
import { Text, TextInput } from '../../components';
import {
	ActionDock,
	Card,
	EmptyState,
	Screen,
	SectionTitle,
	Tag,
	TickRow,
	YesNo,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import {
	PEOPLE_TRAINING_ASSIGNMENT_STATUS,
	PEOPLE_TRAINING_PRACTICAL_OUTCOME,
	PEOPLE_TRAINING_PRACTICAL_OUTCOME_NAMES,
} from '../../../common/constants';
import { normalizePracticalChecklistItems } from '../../../common/utils/people/trainingModules';
import { getEmployeeDisplayName, getTrainingOutcomeLabel } from './trainingDisplay';
import { getHtmlBoldStyle } from '../../utils/htmlBoldStyle';

const RESOLVED_STATUSES = [
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.PASSED,
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.COMPLETED_IN_PERSON,
	PEOPLE_TRAINING_ASSIGNMENT_STATUS.FAILED_REQUIRES_IN_PERSON,
];

const OUTCOME_OPTIONS = [
	PEOPLE_TRAINING_PRACTICAL_OUTCOME.COMPETENT,
	PEOPLE_TRAINING_PRACTICAL_OUTCOME.NEEDS_FOLLOW_UP,
];

const CHECKLIST_HTML_IGNORED_STYLES = [
	'background',
	'background-color',
	'color',
];

const TrainingDetailView = ({ route }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const { width } = useWindowDimensions();
	const assignment = route?.params?.assignment || null;
	const latestResult = assignment?.latestResult || null;
	const isResolved = RESOLVED_STATUSES.includes(assignment?.assignmentStatus);
	const hasExistingOutcome = Boolean(latestResult?.practicalOutcome);
	const canCompletePracticalTraining = Boolean(assignment?.canCompletePracticalTraining);

	const [completePracticalTrainingAssignment, completeStatus] = useMutation(COMPLETE_PRACTICAL_TRAINING_ASSIGNMENT);
	const isSubmitting = completeStatus.loading;

	const checklistItems = useMemo(() => {
		const snapshotItems = normalizePracticalChecklistItems(assignment?.trainingModuleSnapshot?.practicalChecklistItems);
		if (snapshotItems.length) {
			return snapshotItems;
		}

		const resultItems = normalizePracticalChecklistItems(latestResult?.practicalChecklistItems);
		if (resultItems.length) {
			return resultItems;
		}

		return normalizePracticalChecklistItems(assignment?.trainingModule?.practicalChecklistItems);
	}, [
		assignment?.trainingModule?.practicalChecklistItems,
		assignment?.trainingModuleSnapshot?.practicalChecklistItems,
		latestResult?.practicalChecklistItems,
	]);

	const existingCompletionByItemId = useMemo(() => {
		const existingCompletions = latestResult?.practicalChecklistCompletions;
		return (Array.isArray(existingCompletions) ? existingCompletions : []).reduce((acc, item) => {
			const itemId = String(item?.itemId || item?.id || '').trim();
			if (itemId) {
				acc[itemId] = item;
			}
			return acc;
		}, {});
	}, [latestResult?.practicalChecklistCompletions]);

	const [checkedAtByItemId, setCheckedAtByItemId] = useState(() => (
		checklistItems.reduce((acc, item) => {
			const checkedAt = existingCompletionByItemId[item.id]?.checkedAt || '';
			if (checkedAt) {
				acc[item.id] = checkedAt;
			}
			return acc;
		}, {})
	));
	const [outcome, setOutcome] = useState(latestResult?.practicalOutcome || PEOPLE_TRAINING_PRACTICAL_OUTCOME.COMPETENT);
	const [reviewNotes, setReviewNotes] = useState(latestResult?.reviewNotes || '');

	const allItemsChecked = checklistItems.length > 0 &&
		checklistItems.every(item => Boolean(checkedAtByItemId[item.id]));

	const toggleItem = useCallback((itemId) => {
		setCheckedAtByItemId((prevValue) => {
			if (prevValue[itemId]) {
				const nextValue = { ...prevValue };
				delete nextValue[itemId];
				return nextValue;
			}

			return {
				...prevValue,
				[itemId]: new Date().toISOString(),
			};
		});
	}, []);

	const submit = useCallback(async () => {
		if (!assignment?.id || !allItemsChecked) {
			return;
		}

		try {
			await completePracticalTrainingAssignment({
				variables: {
					input: {
						assignmentId: assignment.id,
						checklistCompletions: checklistItems.map(item => ({
							itemId: item.id,
							checked: true,
							checkedAt: checkedAtByItemId[item.id],
						})),
						outcome,
						reviewNotes: reviewNotes || undefined,
					},
				},
			});
			Toast.show('Practical training saved');
			navigation.goBack();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to save practical training');
		}
	}, [
		allItemsChecked,
		assignment?.id,
		checkedAtByItemId,
		checklistItems,
		completePracticalTrainingAssignment,
		navigation,
		outcome,
		reviewNotes,
	]);

	// Page gutters (2 x 18) and the description indent (2 x 14); capped content width.
	const htmlContentWidth = Math.max(1, Math.min(width, ui.layout.maxContentWidth) - 64);
	const htmlBaseFontStyle = useMemo(() => ({
		...ui.type.secondary,
		lineHeight: 19,
		color: ui.colors.textSecondary,
	}), [ui]);
	const htmlTagStyles = useMemo(() => {
		const descriptionText = {
			...ui.type.secondary,
			lineHeight: 19,
			color: ui.colors.textSecondary,
		};
		const bold = getHtmlBoldStyle(ui);

		return {
			p: {
				...descriptionText,
				marginTop: 0,
				marginBottom: 6,
			},
			li: descriptionText,
			strong: bold,
			b: bold,
			a: {
				color: ui.colors.accent,
				textDecorationLine: 'underline',
			},
		};
	}, [ui]);

	if (!assignment) {
		return (
			<Screen backLabel="Training">
				<EmptyState
					tone="error"
					title="Training unavailable"
					message="Go back and open the training again."
				/>
			</Screen>
		);
	}

	const employeeName = getEmployeeDisplayName(assignment.employee);
	const trainerName = latestResult?.trainer?.fullName || '';
	const completedAtLabel = latestResult?.submittedAt
		? moment(latestResult.submittedAt).format('DD MMM YYYY, HH:mm')
		: '';
	const dueLabel = assignment.dueAt ? moment(assignment.dueAt).format('DD MMM YYYY') : '';
	const [competentOutcome, followUpOutcome] = OUTCOME_OPTIONS;
	let outcomeValue = null;
	if (outcome === competentOutcome) {
		outcomeValue = true;
	}
	else if (outcome === followUpOutcome) {
		outcomeValue = false;
	}

	const renderChecklistItem = (item, index) => {
		const checkedAt = checkedAtByItemId[item.id] || '';

		// The description sits outside the tick row so screen readers read it;
		// a row only exposes its title and subtitle.
		return (
			<View key={item.id} style={styles.checklistItem}>
				<TickRow
					title={`${index + 1}. ${item.label}`}
					subtitle={checkedAt ? `Checked ${moment(checkedAt).format('DD MMM YYYY, HH:mm')}` : undefined}
					checked={Boolean(checkedAt)}
					onToggle={canCompletePracticalTraining ? () => toggleItem(item.id) : undefined}
					disabled={isSubmitting}
				/>
				{
					Boolean(item.descriptionHtml) && (
						<View style={styles.checklistItemDescription}>
							<HtmlRenderer
								baseFontStyle={htmlBaseFontStyle}
								imagesMaxWidth={htmlContentWidth}
								html={item.descriptionHtml}
								ignoredStyles={CHECKLIST_HTML_IGNORED_STYLES}
								tagsStyles={htmlTagStyles}
							/>
						</View>
					)
				}
			</View>
		);
	};

	const dock = canCompletePracticalTraining ? (
		<ActionDock
			hint={allItemsChecked ? undefined : 'Check every checklist item to save this training.'}
			action={{
				label: hasExistingOutcome ? 'Update practical training' : 'Save practical training',
				onPress: submit,
				loading: isSubmitting,
				disabled: isSubmitting || !allItemsChecked,
			}}
		/>
	) : null;

	return (
		<Screen backLabel="Training" dock={dock}>
			<View style={styles.titleBlock}>
				<Text style={styles.kicker}>Practical checklist training</Text>
				<Text style={styles.title} accessibilityRole="header">
					{assignment.trainingModule?.title || 'Practical training'}
				</Text>
				<View style={styles.metaRow}>
					{
						Boolean(canCompletePracticalTraining && employeeName) && (
							<Tag label={employeeName} />
						)
					}
					{
						isResolved
							? <Tag label={getTrainingOutcomeLabel(assignment)} tone="success" />
							: <Tag label={dueLabel ? `Due ${dueLabel}` : 'Awaiting completion'} />
					}
				</View>
			</View>

			{
				Boolean(assignment.trainingModule?.description) && (
					<Text style={styles.moduleDescription}>{assignment.trainingModule.description}</Text>
				)
			}

			{
				Boolean(!canCompletePracticalTraining && !isResolved) && (
					<Card style={styles.infoPanel}>
						<Text style={styles.infoText}>
							The Responsible person will check each item off in person.
						</Text>
					</Card>
				)
			}

			{
				Boolean(isResolved && (trainerName || completedAtLabel)) && (
					<Card style={styles.infoPanel}>
						{
							Boolean(trainerName) && (
								<Text style={styles.infoText}>Trainer: {trainerName}</Text>
							)
						}
						{
							Boolean(completedAtLabel) && (
								<Text style={styles.infoText}>Completed {completedAtLabel}</Text>
							)
						}
					</Card>
				)
			}

			{
				checklistItems.length > 0 ? (
					<View style={styles.checklist}>
						{checklistItems.map(renderChecklistItem)}
					</View>
				) : (
					<EmptyState
						icon="list"
						title="No checklist items"
						message="This training module has no checklist content yet."
					/>
				)
			}

			{
				canCompletePracticalTraining ? (
					<>
						<View style={styles.field}>
							<SectionTitle title="Outcome" />
							<YesNo
								value={outcomeValue}
								onChange={nextValue => setOutcome(nextValue ? competentOutcome : followUpOutcome)}
								yesLabel={PEOPLE_TRAINING_PRACTICAL_OUTCOME_NAMES[competentOutcome]}
								noLabel={PEOPLE_TRAINING_PRACTICAL_OUTCOME_NAMES[followUpOutcome]}
								disabled={isSubmitting}
							/>
						</View>

						<View style={styles.field}>
							<SectionTitle title="Notes" />
							<TextInput
								value={reviewNotes}
								onChange={setReviewNotes}
								placeholder="Optional notes visible to the employee"
								multiline={true}
								numberOfLines={4}
								disabled={isSubmitting}
							/>
						</View>
					</>
				) : (
					Boolean(latestResult?.reviewNotes) && (
						<View style={styles.field}>
							<SectionTitle title="Notes from the trainer" />
							<Text style={styles.notesText}>{latestResult.reviewNotes}</Text>
						</View>
					)
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	titleBlock: {
		gap: space.sm,
	},
	kicker: {
		...type.overline,
		color: colors.accent,
	},
	title: {
		...type.title,
		color: colors.text,
	},
	metaRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginTop: space.xs,
	},
	moduleDescription: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	infoPanel: {
		borderLeftWidth: 4,
		borderLeftColor: colors.accent,
		borderRadius: radius.card,
		gap: space.xs,
	},
	infoText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	checklist: {
		gap: space.sm,
	},
	checklistItem: {
		gap: space.xs,
	},
	checklistItemDescription: {
		paddingHorizontal: 14,
	},
	field: {
		gap: space.sm,
	},
	notesText: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

export default TrainingDetailView;
